import { init, browserTracingIntegration, breadcrumbsIntegration } from '@sentry/react';
import type { BrowserClient } from '@sentry/react';
import type { TransactionEvent } from '@sentry/core';

export enum SentryProjects {
    DEV = 'DEV',
    RELEASE = 'RELEASE',
    PRODUCTION = 'PRODUCTION',
}

const GamesSentryDSN: Record<SentryProjects, string> = {
    [SentryProjects.DEV]: "https://f5ff0b4b08dc9999d9b859b13b48403a@o4509112131584001.ingest.de.sentry.io/4509604860526672",
    [SentryProjects.RELEASE]:
        'https://bb0cf163f8f38c350acee1688ec59fa5@o4509112131584001.ingest.de.sentry.io/4509605305188432',
    [SentryProjects.PRODUCTION]:
        'https://30e5feb92c5f2c27cb8cd76b2c9585fb@o4509112131584001.ingest.de.sentry.io/4509605309448272',
} as const;

export function initSentry(release:string, sentryProject: SentryProjects = SentryProjects.DEV) {

    const dsn = GamesSentryDSN[sentryProject];
    const environment = sentryProject.toString().toLowerCase();
    const debug = !(sentryProject === SentryProjects.PRODUCTION);

    const sentryClient = init({
        dsn,
        sendDefaultPii: true, // Personally Identifiable Information (PII)
        integrations: [
            browserTracingIntegration({
                idleTimeout: 60000,
                finalTimeout: 60000,
                traceFetch: false,
                traceXHR: false,
                enableInp: true, // performance metrics
                trackFetchStreamPerformance: false,
                enableHTTPTimings: false,
                _experiments: {
                    enableInteractions: false,
                    enableStandaloneClsSpans: true,
                },
            }),
            breadcrumbsIntegration({
                console: true,
                dom: true,
                fetch: false,
                history: true,
                sentry: true,
                xhr: false,
            }),
            // Sentry.replayIntegration(),
            // Sentry.replayCanvasIntegration(),
            // Sentry.captureConsoleIntegration({ levels: ["log", "error", "warn"] }) // this is optional experimental
        ],
        beforeSendTransaction: (event: TransactionEvent) => {
            // we clean not needed spans from the data
            if (event.spans) {
                event.spans = [];
            }
            return event;
        },
        debug,
        environment,
        release,

        initialScope: {
            tags: {}
        },

        // _experiments: { enableLogs: true },

        // Tracing
        tracesSampleRate: 1.0,
        tracePropagationTargets: [], // Need to link http/https request to other sentry instances via headers in the requests.
        // Session Replay
        replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        profilesSampleRate: 0, // Relates to javascript profiling callstacks, function execution times, etc
    }) as BrowserClient;

    return sentryClient;
}